import { useEffect, useState, type FC } from 'react'

/**
 * A short confirmation in the corner that goes away on its own — "The
 * document has been saved successfully!" (PROJ2-022, PROJ3-002).
 *
 * Raised with `toast()` from anywhere, not from a hook: a delete navigates to
 * the list in the same breath, and the page that said "deleted" has already
 * unmounted by the time it would show. The store lives outside React, so the
 * one `<Toaster />` the shell mounts carries the message across the
 * navigation. With no Toaster mounted (a component test) `toast()` is a no-op.
 */
export type ToastTone = 'good' | 'bad'

export interface ToastMessage {
  readonly id: number
  readonly text: string
  readonly tone: ToastTone
}

type Listener = (all: readonly ToastMessage[]) => void

let shown: readonly ToastMessage[] = []
let nextId = 1
const listeners = new Set<Listener>()
const publish = () => { for (const l of listeners) l(shown) }

/** How long a toast stays, in milliseconds. Long enough to read twice. */
export const TOAST_MS = 4000

export const dismissToast = (id: number): void => {
  shown = shown.filter((t) => t.id !== id)
  publish()
}

/** Shows `text`; returns its id. The same text already showing is not stacked twice. */
export const toast = (text: string, tone: ToastTone = 'good'): number => {
  const same = shown.find((t) => t.text === text)
  if (same) return same.id
  const id = nextId++
  shown = [...shown, { id, text, tone }]
  publish()
  setTimeout(() => dismissToast(id), TOAST_MS)
  return id
}

/** The toasts, drawn once by the app shell. */
export const Toaster: FC = () => {
  const [all, setAll] = useState<readonly ToastMessage[]>(shown)
  useEffect(() => {
    listeners.add(setAll)
    setAll(shown)
    return () => { listeners.delete(setAll) }
  }, [])
  if (all.length === 0) return null
  return (
    <div className="toasts" role="status" aria-live="polite">
      {all.map((t) => (
        <div key={t.id} className={t.tone === 'bad' ? 'toast toast--bad' : 'toast'}>
          <span className="toast__text">{t.text}</span>
          <button type="button" className="toast__close" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>×</button>
        </div>
      ))}
    </div>
  )
}
